import { getApiBaseUrl } from './apiBaseUrl';

/**
 * Fire-and-forget signup-event telemetry. Backend stores rows in
 * signup_events for admin visibility. NEVER throws - telemetry must
 * never block or surface to the user.
 */
export interface SignupEventPayload {
  firebase_uid?: string | null;
  email?: string | null;
  provider: 'email' | 'google';
  verification_sent?: boolean | null;
  error_code?: string | null;
  error_message?: string | null;
  // Extended browser/user context for admin auditing (admin request 2026-05-19).
  display_name?: string | null;
  photo_url?: string | null;
  language?: string | null;
  timezone?: string | null;
  platform?: string | null;
}

/**
 * Best-effort browser environment snapshot - runs in user's browser, all
 * fields are surfaceable to JS and not considered private. Wrapped in
 * try/catch so a missing API in older browsers never breaks signup.
 */
export function collectBrowserContext(): {
  language: string | null;
  timezone: string | null;
  platform: string | null;
} {
  let language: string | null = null;
  let timezone: string | null = null;
  let platform: string | null = null;

  try {
    if (typeof navigator !== 'undefined') {
      language = navigator.language || null;
      // navigator.platform deprecated; prefer userAgentData when available.
      const uaData = (navigator as unknown as { userAgentData?: { platform?: string } }).userAgentData;
      platform = (uaData?.platform || navigator.platform || null) as string | null;
    }
  } catch {
    /* noop */
  }
  try {
    if (typeof Intl !== 'undefined') {
      timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || null;
    }
  } catch {
    /* noop */
  }
  return { language, timezone, platform };
}

/**
 * Fire-and-forget signup telemetry.
 *
 * `idToken` (optional) is a Firebase ID token for the just-created/just-
 * authenticated user. When present we attach it as `Authorization: Bearer
 * <token>` so the backend can re-derive identity from the verified token
 * rather than trusting the client-supplied `firebase_uid`/`email` fields
 * (security finding C.6). Pre-auth FAILURE events have no token yet - callers
 * pass nothing and the request goes out unauthenticated, exactly as before.
 *
 * The token is used only for the header; it is never written into the JSON
 * body. This is a raw `fetch` (not the axios `api` instance) on purpose so it
 * can use `keepalive` and stay fully decoupled from the request queue - hence
 * the token is threaded in explicitly instead of relying on an interceptor.
 */
export async function postSignupEvent(
  payload: SignupEventPayload,
  idToken?: string | null
): Promise<void> {
  try {
    const base = getApiBaseUrl();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (idToken) {
      headers.Authorization = `Bearer ${idToken}`;
    }
    await fetch(`${base}/api/auth/signup-event`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
      // Use keepalive so the request survives page navigation that often
      // follows a successful signup (modal closes, route changes).
      keepalive: true,
    });
  } catch {
    // Swallow - telemetry never fails the user-facing flow.
  }
}
